import { useState, useEffect } from 'react';
import { usePosts } from '../../../hooks/post/usePosts';

import ProfileTabs from './ProfileTabs';
import ProfilePostsShow from './ProfilePostsShow';
// import ProfileAchivs from './ProfileAchivs';
import ProfileInfo from './ProfileInfo';
import ProfileButtons from './ProfileButtons';
import ProfileAvatar from './ProfileAvatar';

import PostsSkeleton from '../PostsSkeleton/PostsSkeleton';
import ServerError, { NotFound } from '../ServerError/ServerError';
import Sentinel from '../InfiniteScroll/Sentinel';
import { Button } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { CaretLeft } from '@phosphor-icons/react';
import { useBlock } from '../../../hooks/service/useBlock';
import { useAuth } from '../../../hooks/auth/useAuth';
import { useChatContentCounts } from '../../../hooks/messages/useChatContent';
import SegmentControl from '../../ui/SegmentControl';
import ProfileChatContent from './ProfileChatContent';

function ProfileContentShow({ user, side = false, restriction = {} }) {
    const navigate = useNavigate();
    const { isAuth } = useAuth();

    const [tab, setTab] = useState(localStorage.getItem('profileTabs') ?? 'posts');
    // Раздел профиля собеседника: записи или содержимое переписки с ним.
    const [screen, setScreen] = useState('posts');
    const [initialPostCount, setInitialPostCount] = useState(null);
    // Витрина живёт здесь, а не в шапке: выбирают её в модалке достижений
    // (ProfileInfo), а рисуется она вокруг аватара (ProfileAvatar) — иначе
    // значки перестроились бы только после перезагрузки профиля.
    const [showcase, setShowcase] = useState([]);

    const postsData = usePosts({ idUser: user.id, side, category: 'posts' });
    const likesData = usePosts({ idUser: user.id, side, category: 'likes' });
    const favoritesData = usePosts({ idUser: user.id, side, category: 'bookmarks' });
    const { blocked, loading: blockLoading, toggle: toggleBlock } = useBlock(user?.id, side && !user?.itsme);

    const handlerChangeTab = (tab) => {
        tab = ['posts', 'likes', 'bookmarks'].includes(tab) ? tab : 'posts'
        localStorage.setItem('profileTabs', tab)
        setTab(tab)
    }

    const tabMap = {
        posts: postsData,
        likes: likesData,
        bookmarks: favoritesData,
    };

    const currentTabData = tabMap[tab] ?? {};
    const { posts = [], error = null, loading = false, hasMore = false, loadingMore = false, loadMore = () => { } } = currentTabData;

    // Разделы по переписке имеют смысл только при взгляде со стороны: в своём
    // профиле уже есть переключатель «Записи / Лайки / Избранное», и два
    // таббара подряд — это каша.
    const { counts, hasContent } = useChatContentCounts({ userId: user?.id }, isAuth && side);

    // Вкладку показываем, только если в этой категории что-то есть: пустой
    // раздел, в который нечего открыть, раздражает больше, чем его отсутствие.
    const screenOptions = [
        { value: 'posts', label: 'Записи' },
        ...(counts.media > 0 ? [{ value: 'media', label: 'Медиа' }] : []),
        ...(counts.files > 0 ? [{ value: 'files', label: 'Файлы' }] : []),
        ...(counts.links > 0 ? [{ value: 'links', label: 'Ссылки' }] : []),
    ];

    const showContentTabs = side && hasContent;
    const currentScreen = showContentTabs ? screen : 'posts';

    useEffect(() => {
        setInitialPostCount(user?.countPosts);
        setShowcase(Array.isArray(user?.showcaseAchievements) ? user.showcaseAchievements : []);
    }, [user]);

    const getCountFromJson = (json) =>
        Array.isArray(json) ? json.length : 0;

    return (
        <>
            <ProfileAvatar
                avatar={user?.avatar ?? null}
                cap={user?.cap ?? null}
                firstName={user?.firstName ?? null}
                lastName={user?.lastName ?? null}
                verified={user?.verified}
                description={user?.description ?? null}
                userId={user?.itsme ? null : (user?.id ?? null)}
                username={user?.username ?? null}
                achievements={showcase}
            />

            <ProfileInfo
                countSubs={getCountFromJson(user?.jsonSubs)}
                countSub={getCountFromJson(user?.jsonSub)}
                countAchivs={getCountFromJson(user?.jsonAchivs)}
                countPosts={initialPostCount ?? 0}
                userId={user?.id}
                setTab={handlerChangeTab}
                isAuth={isAuth}
                onShowcaseChange={setShowcase}
            />

            {isAuth && (
                <ProfileButtons
                    username={user?.username ?? null}
                    id={user?.id ?? 0}
                    firstName={user?.firstName ?? null}
                    lastName={user?.lastName ?? null}
                    avatar={user?.avatar ?? null}
                    side={side}
                    itsme={user?.itsme ?? false}
                    restriction={restriction}
                    blocked={blocked}
                    blockLoading={blockLoading}
                    toggleBlock={toggleBlock}
                />
            )}

            {!side && <ProfileTabs tab={tab} setTab={handlerChangeTab} />}

            {showContentTabs && (
                <SegmentControl
                    value={currentScreen}
                    options={screenOptions}
                    onChange={setScreen}
                    layoutId="profile-content-tab"
                    sx={{ mb: 1 }}
                />
            )}

            {currentScreen !== 'posts' ? (
                <ProfileChatContent source={{ userId: user?.id }} type={currentScreen} />
            ) : loading ? (
                <PostsSkeleton count={3} />
            ) : error ? (
                <ServerError />
            ) : Array.isArray(posts) && posts.length > 0 ? (
                <>
                    <ProfilePostsShow posts={posts} side={side} tab={tab} />
                    <Sentinel onLoadMore={loadMore} hasMore={hasMore} loading={loadingMore} />
                </>
            ) : (
                <NotFound
                    buttonShow={!side && tab === 'posts'}
                    buttonName="Создать запись"
                    buttonLink="/post/create"
                />
            )}
        </>
    );
}

export default ProfileContentShow;
